import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Menu from "../../components/layout/Menu.tsx";
import Footer from "../../components/layout/Footer.tsx";
import api from "../../api.ts";
import toast from "react-hot-toast";
import {
  FiClock,
  FiMonitor,
  FiUser,
  FiSave,
  FiPauseCircle,
  FiRotateCcw,
} from "react-icons/fi";
import { IoCloseSharp } from "react-icons/io5";
import { MdRefresh } from "react-icons/md";
import {
  FaLaptopCode,
  FaCheckCircle,
  FaPlayCircle,
  FaStar,
} from "react-icons/fa";
import LoadingSpinner from "../../components/ui/LoadingSpinner.tsx";
import { Estrelas, ROTULOS_NOTA } from "../../components/tickets/TicketRating.tsx";
import {
  TicketType,
  TIPOS_CHAMADO,
  TIPOS_DO_ATENDENTE,
  TIPOS_LEGADOS,
  tipoInfo,
} from "../../components/tickets/ticketTypes.ts";

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface Tenant {
  id: number;
  name: string;
}

interface TicketAttachment {
  id: number;
  file_url: string;
  file_name: string | null;
  file_type: string | null;
}

interface Ticket {
  id: number;
  tenant_id: number;
  tenant_name: string;
  name: string;
  /** Classificação do atendente; null enquanto está em "Novo". */
  type: TicketType | null;
  classified_at?: string | null;
  classified_by_name?: string | null;
  title: string;
  description: string;
  origin_url: string;
  status: string;
  created_at: string;
  attendant_id: number | null;
  attendant_name: string | null;
  resolution_notes: string | null;
  requester_email: string | null;
  observation: string | null;
  attachments?: TicketAttachment[];
  /** Avaliação do solicitante (1–5), depois de concluído. */
  rating?: number | null;
  rating_comment?: string | null;
  rated_at?: string | null;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const COLUMNS: { key: string; label: string }[] = [
  { key: "novo", label: "Novo" },
  { key: "andamento", label: "Em Andamento" },
  { key: "concluido", label: "Concluído" },
  { key: "pausado", label: "Pausado" },
];

const getInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

// Mensagem de erro que o backend devolve (ex.: 409 de classificação).
const erroDaApi = (err: unknown, padrao: string) =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
  padrao;

// ─── Card ───────────────────────────────────────────────────────────────────

const TicketCard: React.FC<{
  ticket: Ticket;
  onClick: (f: Ticket) => void;
}> = ({ ticket, onClick }) => {
  const typeInfo = tipoInfo(ticket.type);
  const TypeIcon = typeInfo.Icon;

  const formattedDate = new Date(ticket.created_at).toLocaleDateString(
    "pt-BR",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    },
  );

  return (
    <div className="ticket-card" onClick={() => onClick(ticket)}>
      <div className="ticket-card-accent" />

      <div className="ticket-card-header">
        <span
          className={`ticket-type-badge ${ticket.type ? "" : "ticket-type-badge--pendente"}`}
          style={{ "--badge-color": typeInfo.color } as React.CSSProperties}
        >
          <TypeIcon className="ticket-type-icon" aria-hidden="true" />
          {typeInfo.label}
        </span>
        <span className="ticket-card-id">#{ticket.id}</span>
      </div>

      <p className="ticket-card-title">{ticket.title}</p>

      {ticket.description && (
        <p className="ticket-card-desc">
          {ticket.description.length > 90
            ? ticket.description.slice(0, 90) + "..."
            : ticket.description}
        </p>
      )}

      <div className="ticket-card-footer">
        <span className="ticket-card-requester">{ticket.name}</span>
        {ticket.rating ? (
          <span
            className="ticket-card-rating"
            title={`Avaliado com ${ticket.rating} de 5`}
            aria-label={`Avaliado com ${ticket.rating} de 5`}
          >
            <FaStar aria-hidden="true" />
            {ticket.rating}
          </span>
        ) : null}
        <span className="ticket-card-date">
          <FiClock size={11} />
          {formattedDate}
        </span>
      </div>
    </div>
  );
};

// ─── Coluna ───────────────────────────────────────────────────────────────────

const KanbanColumn: React.FC<{
  column: { key: string; label: string };
  tickets: Ticket[];
  onCardClick: (f: Ticket) => void;
}> = ({ column, tickets, onCardClick }) => {
  return (
    <div className="hd-kanban-column">
      <div className="hd-kanban-column-header">
        <div className="hd-kanban-column-header-left">
          <span className="hd-kanban-column-title">{column.label}</span>
        </div>
        <span className="hd-kanban-column-count">{tickets.length}</span>
      </div>

      <div className="hd-kanban-cards">
        {tickets.length === 0 ? (
          <div className="hd-kanban-empty">
            <span>Nenhum ticket aqui</span>
          </div>
        ) : (
          tickets.map((f) => (
            <TicketCard key={f.id} ticket={f} onClick={onCardClick} />
          ))
        )}
      </div>
    </div>
  );
};

// ─── Página principal ─────────────────────────────────────────────────────────

const TicketKanbanPage: React.FC = () => {
  const navigate = useNavigate();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("");
  // O que está digitado x o que já foi para a busca: o debounce separa os
  // dois para não disparar uma requisição a cada tecla.
  const [requesterInput, setRequesterInput] = useState("");
  const [titleInput, setTitleInput] = useState("");
  const [requesterQuery, setRequesterQuery] = useState("");
  const [titleQuery, setTitleQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [detailTicket, setDetailTicket] = useState<Ticket | null>(null);
  const [attending, setAttending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [moving, setMoving] = useState(false);
  // Classificação escolhida no modal para iniciar o atendimento.
  const [tipoEscolhido, setTipoEscolhido] = useState<TicketType | null>(null);
  const [classificando, setClassificando] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [observation, setObservation] = useState("");

  const fetchTickets = async () => {
    try {
      const params: Record<string, string> = {};
      if (selectedTenant) params.tenant_id = selectedTenant;
      if (selectedType) params.type = selectedType;
      if (requesterQuery.trim()) params.requester = requesterQuery.trim();
      if (titleQuery.trim()) params.search = titleQuery.trim();

      const res = await api.get("/api/tickets", { params });
      setTickets(res.data);
      setLoadError(false);
    } catch {
      setLoadError(true);
      toast.error("Erro ao carregar tickets.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const res = await api.get("/api/widget-tenants");
      setTenants(res.data);
    } catch {}
  };

  useEffect(() => {
    fetchTenants();
  }, []);
  // Espera a digitação parar antes de consultar o servidor.
  useEffect(() => {
    const t = setTimeout(() => {
      setRequesterQuery(requesterInput);
      setTitleQuery(titleInput);
    }, 400);
    return () => clearTimeout(t);
  }, [requesterInput, titleInput]);

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant, selectedType, requesterQuery, titleQuery]);

  // Recarrega mantendo os filtros atuais. O estado próprio evita piscar a
  // tela inteira de "Carregando..." a cada atualização manual.
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTickets();
    setRefreshing(false);
  };

  const openDetail = (t: Ticket) => {
    setDetailTicket(t);
    setResolutionNotes(t.resolution_notes || "");
    setObservation(t.observation || "");
    setTipoEscolhido(null);
  };

  // Aplica o ticket atualizado tanto no board quanto no modal aberto.
  // Mescla em vez de substituir: um campo que a resposta não trouxer continua
  // valendo o que já estava na tela, em vez de virar undefined.
  const applyUpdate = (updated: Ticket) => {
    setTickets((prev) =>
      prev.map((f) => (f.id === updated.id ? { ...f, ...updated } : f)),
    );
    setDetailTicket((atual) =>
      atual && atual.id === updated.id ? { ...atual, ...updated } : updated,
    );
  };

  // Novo → Em Andamento (registra o atendente)
  // A classificação é obrigatória: quem classifica é o atendente, não o
  // solicitante.
  const handleStartAttending = async () => {
    if (!detailTicket) return;
    if (!tipoEscolhido) {
      toast.error("Classifique o chamado para iniciar o atendimento.");
      return;
    }
    setAttending(true);
    try {
      const res = await api.patch(`/api/tickets/${detailTicket.id}/attend`, {
        type: tipoEscolhido,
      });
      applyUpdate(res.data);
      toast.success("Atendimento iniciado.");
    } catch (err) {
      toast.error(erroDaApi(err, "Erro ao iniciar atendimento."));
    } finally {
      setAttending(false);
    }
  };

  // Reclassificar durante o atendimento (andamento ou pausado).
  const handleReclassificar = async (type: TicketType) => {
    if (!detailTicket || type === detailTicket.type) return;
    setClassificando(true);
    try {
      const res = await api.patch(
        `/api/tickets/${detailTicket.id}/classificacao`,
        { type },
      );
      applyUpdate(res.data);
      toast.success(`Chamado reclassificado como ${TIPOS_CHAMADO[type].label}.`);
    } catch (err) {
      toast.error(erroDaApi(err, "Erro ao reclassificar o chamado."));
    } finally {
      setClassificando(false);
    }
  };

  // Em Andamento → Concluído (envia e-mail ao solicitante)
  const handleCloseAttendance = async () => {
    if (!detailTicket) return;
    setClosing(true);
    try {
      const res = await api.patch(`/api/tickets/${detailTicket.id}/close`, {
        resolution_notes: resolutionNotes,
      });
      applyUpdate(res.data);
      toast.success("Atendimento concluído. E-mail enviado ao solicitante.");
    } catch {
      toast.error("Erro ao concluir atendimento.");
    } finally {
      setClosing(false);
    }
  };

  // Movimentações simples de status (pausar / retomar)
  const changeStatus = async (newStatus: string, successMsg: string) => {
    if (!detailTicket) return;
    setMoving(true);
    try {
      const res = await api.patch(`/api/tickets/${detailTicket.id}`, {
        status: newStatus,
      });
      applyUpdate(res.data);
      toast.success(successMsg);
    } catch {
      toast.error("Erro ao mover o chamado.");
    } finally {
      setMoving(false);
    }
  };

  const handlePause = () => changeStatus("pausado", "Chamado pausado.");
  const handleResume = () =>
    changeStatus("andamento", "Chamado retomado.");

  const closeDetail = () => {
    const ticket = detailTicket;
    const obs = observation;
    setDetailTicket(null);
    // Salva a observação automaticamente se ela foi alterada
    if (ticket && obs !== (ticket.observation || "")) {
      api
        .patch(`/api/tickets/${ticket.id}`, { observation: obs })
        .then((res) => {
          const updated: Ticket = res.data;
          setTickets((prev) =>
            prev.map((f) => (f.id === updated.id ? { ...f, ...updated } : f)),
          );
        })
        .catch(() => toast.error("Erro ao salvar observação."));
    }
  };

  // Ordena os cards por data de criação: mais antigos primeiro em todas as
  // etapas, exceto "Concluído", que mostra os mais recentes primeiro.
  const ticketsByStatus = (status: string) =>
    tickets
      .filter((f) => f.status === status)
      .sort((a, b) => {
        const diff =
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        return status === "concluido" ? -diff : diff;
      });

  const isBeingAttended = detailTicket?.attendant_id != null;
  const detailType = tipoInfo(detailTicket?.type);
  const DetailTypeIcon = detailType.Icon;

  return (
    <div className="p-2">
      <Menu />
      <div className="content-area">
        <div className="page-container">
          {/* Header */}
          <div className="page-header">
            <h1 className="page-title">Central de Chamados</h1>
            <div className="page-actions">
              <button
                className="form-icon-edit"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <MdRefresh />
                {refreshing ? "Atualizando..." : "Atualizar"}
              </button>
              <button
                className="form-button btn-icon-text"
                onClick={() => navigate("/admin/widget-tenants")}
              >
                <FaLaptopCode size={20} />
                Sistemas Integrados
              </button>
            </div>
          </div>

          {/* Filtros */}
          <div className="page-filters">
            <div className="filter-group">
              <label className="filter-label" htmlFor="filtro-sistema">
                Tipo de Sistema
              </label>
              <select
                id="filtro-sistema"
                className="form-select"
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
              >
                <option value="">Todos os sistemas</option>
                {tenants.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label" htmlFor="filtro-tipo">
                Tipo de Chamado
              </label>
              <select
                id="filtro-tipo"
                className="form-select"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="">Todos os tipos</option>
                <option value="sem_tipo">A classificar</option>
                {TIPOS_DO_ATENDENTE.map((t) => (
                  <option key={t} value={t}>
                    {TIPOS_CHAMADO[t].label}
                  </option>
                ))}
                <optgroup label="Antigos (escolhidos pelo solicitante)">
                  {TIPOS_LEGADOS.map((t) => (
                    <option key={t} value={t}>
                      {TIPOS_CHAMADO[t].label}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label" htmlFor="filtro-solicitante">
                Solicitante
              </label>
              <input
                id="filtro-solicitante"
                type="text"
                className="form-input"
                placeholder="Nome, e-mail ou telefone"
                value={requesterInput}
                onChange={(e) => setRequesterInput(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label" htmlFor="filtro-titulo">
                Título ou Código
              </label>
              <input
                id="filtro-titulo"
                type="text"
                className="form-input"
                placeholder="#42 ou palavras do título"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
              />
            </div>
          </div>

          {/* Board */}
          <div className="hd-kanban-container">
            {loading ? (
              <LoadingSpinner variant="inline" label="Carregando chamados" />
            ) : (
              <>
              {loadError && (
                <div className="tela-loading">
                  Não foi possível carregar os dados. Tente novamente mais tarde.
                </div>
              )}
              <div className="hd-kanban-board">
                {COLUMNS.map((col) => (
                  <KanbanColumn
                    key={col.key}
                    column={col}
                    tickets={ticketsByStatus(col.key)}
                    onCardClick={openDetail}
                  />
                ))}
              </div>
              </>
            )}
          </div>
        </div>

        {/* Modal de detalhes */}
        {detailTicket && (
          <div className="modal-overlay" onClick={closeDetail}>
            <div
              className="detail-ticket-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Cabeçalho */}
              <div className="ticket-modal-topbar">
                <div className="ticket-modal-topbar-left">
                  <span
                    className={`ticket-type-badge ticket-type-badge--lg ${
                      detailTicket.type ? "" : "ticket-type-badge--pendente"
                    }`}
                    style={
                      {
                        "--badge-color": detailType.color,
                      } as React.CSSProperties
                    }
                  >
                    <DetailTypeIcon
                      className="ticket-type-icon"
                      aria-hidden="true"
                    />
                    {detailType.label}
                  </span>
                  <span className="ticket-modal-id">#{detailTicket.id}</span>
                </div>
                <button
                  className="modal-close-button"
                  onClick={closeDetail}
                  aria-label="Fechar"
                >
                  <IoCloseSharp />
                </button>
              </div>

              {/* Corpo em duas colunas */}
              <div className="ticket-modal-body">
                <div className="kanban-modal-left">
                  <h2 className="kanban-modal-title">{detailTicket.title}</h2>

                  {detailTicket.description && (
                    <div className="ticket-modal-field">
                      <span className="ticket-modal-field-label">
                        Descrição
                      </span>
                      <p className="kanban-modal-desc">
                        {detailTicket.description}
                      </p>
                    </div>
                  )}

                  {detailTicket.attachments &&
                    detailTicket.attachments.length > 0 && (
                      <div className="ticket-modal-field">
                        <span className="ticket-modal-field-label">Anexos</span>
                        <ul className="chamado-attachments">
                          {detailTicket.attachments.map((att) => (
                            <li key={att.id}>
                              <a
                                href={att.file_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {att.file_name || "arquivo"}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  <div className="ticket-modal-meta">
                    {detailTicket.name && (
                      <div className="ticket-modal-meta-row">
                        <FiUser
                          className="ticket-modal-meta-icon"
                          aria-hidden="true"
                        />
                        <span className="ticket-modal-meta-label">
                          Solicitante
                        </span>
                        <span className="ticket-modal-meta-value">
                          {detailTicket.name}
                        </span>
                      </div>
                    )}
                    <div className="ticket-modal-meta-row">
                      <FiMonitor
                        className="ticket-modal-meta-icon"
                        aria-hidden="true"
                      />
                      <span className="ticket-modal-meta-label">Sistema</span>
                      <span className="ticket-modal-meta-value">
                        {detailTicket.tenant_name}
                      </span>
                    </div>
                    <div className="ticket-modal-meta-row">
                      <FiClock
                        className="ticket-modal-meta-icon"
                        aria-hidden="true"
                      />
                      <span className="ticket-modal-meta-label">Recebido</span>
                      <span className="ticket-modal-meta-value">
                        {new Date(detailTicket.created_at).toLocaleString(
                          "pt-BR",
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="kanban-modal-right">
                  {/* Atendente atual (discreto) */}
                  {isBeingAttended && detailTicket.attendant_name && (
                    <div className="ticket-modal-attendant">
                      <span className="ticket-modal-avatar">
                        {getInitials(detailTicket.attendant_name)}
                      </span>
                      <div className="ticket-modal-attendant-info">
                        <span className="ticket-modal-attendant-label">
                          Atendente
                        </span>
                        <span className="ticket-modal-attendant-name">
                          {detailTicket.attendant_name}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Ações do fluxo — dependem da etapa atual do card */}
                  {detailTicket.status === "novo" && (
                    <div className="kanban-classificacao">
                      <span
                        className="kanban-modal-resolution-label"
                        id="classificacao-label"
                      >
                        Classificação
                      </span>
                      <span className="kanban-classificacao-hint">
                        Escolha o tipo do chamado para iniciar o atendimento.
                      </span>
                      <div
                        className="kanban-classificacao-opcoes"
                        role="radiogroup"
                        aria-labelledby="classificacao-label"
                      >
                        {TIPOS_DO_ATENDENTE.map((t) => {
                          const info = TIPOS_CHAMADO[t];
                          const Icon = info.Icon;
                          const marcado = tipoEscolhido === t;
                          return (
                            <button
                              key={t}
                              type="button"
                              role="radio"
                              aria-checked={marcado}
                              className={`kanban-classificacao-opcao ${marcado ? "selected" : ""}`}
                              style={
                                { "--badge-color": info.color } as React.CSSProperties
                              }
                              onClick={() => setTipoEscolhido(t)}
                              disabled={attending}
                            >
                              <Icon className="ticket-type-icon" aria-hidden="true" />
                              <span className="kanban-classificacao-texto">
                                <strong>{info.label}</strong>
                                <small>{info.descricao}</small>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <button
                        className="ticket-modal-action-btn btn-icon-text"
                        onClick={handleStartAttending}
                        disabled={attending || !tipoEscolhido}
                      >
                        <FaPlayCircle />
                        {attending ? "Iniciando..." : "Iniciar atendimento"}
                      </button>
                    </div>
                  )}

                  {(detailTicket.status === "andamento" ||
                    detailTicket.status === "pausado") && (
                    <div className="kanban-classificacao">
                      <label
                        className="kanban-modal-resolution-label"
                        htmlFor="reclassificar"
                      >
                        Classificação
                      </label>
                      <select
                        id="reclassificar"
                        className="form-select"
                        value={detailTicket.type ?? ""}
                        disabled={classificando}
                        onChange={(e) =>
                          handleReclassificar(e.target.value as TicketType)
                        }
                      >
                        {/* Tipo antigo (do solicitante) aparece, mas não é
                            mais oferecido: ao trocar, sai da lista. */}
                        {detailTicket.type &&
                          TIPOS_CHAMADO[detailTicket.type]?.legado && (
                            <option value={detailTicket.type} disabled>
                              {TIPOS_CHAMADO[detailTicket.type].label} (antigo)
                            </option>
                          )}
                        {TIPOS_DO_ATENDENTE.map((t) => (
                          <option key={t} value={t}>
                            {TIPOS_CHAMADO[t].label}
                          </option>
                        ))}
                      </select>
                      {detailTicket.classified_by_name && (
                        <span className="kanban-classificacao-hint">
                          Classificado por {detailTicket.classified_by_name}
                        </span>
                      )}
                    </div>
                  )}

                  {detailTicket.status === "andamento" && (
                    <div className="ticket-modal-actions">
                      <button
                        className="ticket-modal-action-btn btn-icon-text"
                        onClick={handleCloseAttendance}
                        disabled={closing || moving}
                      >
                        <FaCheckCircle />
                        {closing ? "Concluindo..." : "Concluir atendimento"}
                      </button>
                      <button
                        className="ticket-modal-action-btn ticket-modal-action-btn--secondary btn-icon-text"
                        onClick={handlePause}
                        disabled={closing || moving}
                      >
                        <FiPauseCircle />
                        Pausar atendimento
                      </button>
                    </div>
                  )}

                  {detailTicket.status === "concluido" && (
                    <div className="kanban-modal-concluded">
                      <FaCheckCircle />
                      Chamado concluído
                    </div>
                  )}

                  {detailTicket.status === "concluido" && detailTicket.type && (
                    <span className="kanban-classificacao-hint">
                      {detailTicket.classified_by_name
                        ? `Classificado como ${detailType.label} por ${detailTicket.classified_by_name}`
                        : `Tipo ${detailType.label}, escolhido pelo solicitante`}
                    </span>
                  )}

                  {detailTicket.status === "concluido" && (
                    <div className="kanban-modal-rating">
                      <span className="kanban-modal-rating-title">
                        Avaliação do solicitante
                      </span>
                      {detailTicket.rating ? (
                        <>
                          <div className="kanban-modal-rating-row">
                            <Estrelas nota={detailTicket.rating} />
                            <span>{ROTULOS_NOTA[detailTicket.rating]}</span>
                          </div>
                          {detailTicket.rating_comment && (
                            <p className="kanban-modal-rating-comment">
                              “{detailTicket.rating_comment}”
                            </p>
                          )}
                          {detailTicket.rated_at && (
                            <span className="kanban-modal-rating-date">
                              em{" "}
                              {new Date(detailTicket.rated_at).toLocaleDateString("pt-BR")}
                            </span>
                          )}
                        </>
                      ) : (
                        <p className="kanban-modal-rating-empty">Ainda não avaliado.</p>
                      )}
                    </div>
                  )}

                  {detailTicket.status === "pausado" && (
                    <button
                      className="ticket-modal-action-btn ticket-modal-action-btn--secondary btn-icon-text"
                      onClick={handleResume}
                      disabled={moving}
                    >
                      <FiRotateCcw />
                      Retomar atendimento
                    </button>
                  )}

                  {/* Instruções de resolução — editáveis durante o atendimento */}
                  {detailTicket.status !== "novo" && (
                    <div className="kanban-modal-resolution">
                      <label className="kanban-modal-resolution-label">
                        Instruções de resolução
                      </label>
                      <textarea
                        className="kanban-modal-resolution-textarea"
                        placeholder="Descreva os passos tomados para resolver este chamado."
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        disabled={detailTicket.status !== "andamento"}
                        rows={4}
                      />
                    </div>
                  )}

                  {/* Observação — salva automaticamente ao fechar */}
                  <div className="kanban-modal-resolution">
                    <div className="ticket-modal-obs-head">
                      <label className="kanban-modal-resolution-label">
                        Observação
                      </label>
                      <span className="ticket-modal-obs-hint">
                        <FiSave aria-hidden="true" />
                        salva ao sair
                      </span>
                    </div>
                    <textarea
                      className="kanban-modal-resolution-textarea"
                      placeholder="Anotações internas sobre o chamado."
                      value={observation}
                      onChange={(e) => setObservation(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default TicketKanbanPage;
